import { InteractionSchemeAddress } from 'common/types'

/**
 * When the in-person trainer's agenda starts by itself instead of waiting for
 * the Start script button. Everything here is a plain value, so the rule can
 * be read - and tested - without a room around it.
 *
 * Starting is not idempotent: the server's `start` and `next` are the same
 * request, and the second of two starts moves the agenda to item 2. So the
 * rule leans one way throughout - when anything is unknown, it does not start,
 * and the Start script button stays the trainer's way in.
 */
export type AutoStartInput = {
	/** An in-person training. Online keeps the Start script button. */
	inPerson: boolean
	/** The training's own trainer, not a participant or another facilitator. */
	isTrainer: boolean
	/** The presentation window, which never talks to the backend. */
	projector: boolean
	/** The script state has arrived from the server (`selectScriptLoaded`). */
	loaded: boolean
	/** The script has moved off "not yet started" (`selectScriptStarted`). */
	started: boolean
	/** The chunk the script starts in has items (`selectScriptHasItems`). */
	hasItems: boolean
	/** This page has already tried to start this training's script. */
	alreadyAttempted: boolean
}

export const shouldAutoStartScript = ({
	inPerson,
	isTrainer,
	projector,
	loaded,
	started,
	hasItems,
	alreadyAttempted,
}: AutoStartInput): boolean =>
	inPerson &&
	isTrainer &&
	!projector &&
	loaded &&
	!started &&
	hasItems &&
	!alreadyAttempted

/**
 * What one attempt is counted against: the interaction scheme, not the room.
 * Walking to the break room and back is the same scheme, so it is no new
 * entry; a blended training that rebuilds the scheme for its next chunk is.
 */
export const autoStartKey = ({
	workflowId,
	interactionSchemeId,
}: InteractionSchemeAddress): string => `${workflowId}/${interactionSchemeId}`
